import { Navigate, Route, Routes } from "react-router-dom";
import { TenantLayout } from "@/layouts/TenantLayout";
import { LoginPage } from "@/feature-module/auth/LoginPage";
import { AuthHandoffPage } from "@/feature-module/auth/AuthHandoffPage";
import { AdminMenuVisibilityPage } from "@/feature-module/tenant/AdminMenuVisibilityPage";
import { AdmissionsPage } from "@/feature-module/tenant/AdmissionsPage";
import { AppointmentsPage } from "@/feature-module/tenant/AppointmentsPage";
import { EmergencyPage } from "@/feature-module/tenant/EmergencyPage";
import { PatientDetailPage } from "@/feature-module/tenant/PatientDetailPage";
import { PatientRecordsPage } from "@/feature-module/tenant/PatientRecordsPage";
import { PatientsListPage } from "@/feature-module/tenant/PatientsListPage";
import { PharmacyBillingPage } from "@/feature-module/tenant/PharmacyBillingPage";
import { PharmacyDashboardPage } from "@/feature-module/tenant/PharmacyDashboardPage";
import { FinancialDashboardPage } from "@/feature-module/tenant/FinancialDashboardPage";
import { OperationalDashboardPage } from "@/feature-module/tenant/OperationalDashboardPage";
import { SurgeryPage } from "@/feature-module/tenant/SurgeryPage";
import { AmbulanceTransportsPage } from "@/feature-module/tenant/AmbulanceTransportsPage";
import { HospitalServicesHubPage } from "@/feature-module/tenant/HospitalServicesHubPage";
import { DirectionHubPage } from "@/feature-module/tenant/DirectionHubPage";
import { PharmacyInvoiceHistoryPage } from "@/feature-module/tenant/PharmacyInvoiceHistoryPage";
import { PharmacyInvoicePage } from "@/feature-module/tenant/PharmacyInvoicePage";
import { PharmacyOrdersPage } from "@/feature-module/tenant/PharmacyOrdersPage";
import { PharmacyOrderDetailPage } from "@/feature-module/tenant/PharmacyOrderDetailPage";
import { BillingAccountingPage } from "@/feature-module/tenant/BillingAccountingPage";
import { CashierPage } from "@/feature-module/tenant/CashierPage";
import { InsuranceCaseDetailPage } from "@/feature-module/tenant/insurance/InsuranceCaseDetailPage";
import { InsuranceCasesPage } from "@/feature-module/tenant/insurance/InsuranceCasesPage";
import { InsuranceHomePage } from "@/feature-module/tenant/insurance/InsuranceHomePage";
import { InsuranceInsurerDetailPage } from "@/feature-module/tenant/insurance/InsuranceInsurerDetailPage";
import { InsuranceInsurersPage } from "@/feature-module/tenant/insurance/InsuranceInsurersPage";
import { InsurancePartnershipsPage } from "@/feature-module/tenant/insurance/InsurancePartnershipsPage";
import { PharmacyStockArticlesPage } from "@/feature-module/tenant/PharmacyStockArticlesPage";
import { PharmacyStockScanPage } from "@/feature-module/tenant/PharmacyStockScanPage";
import { ConsultationDetailPage } from "@/feature-module/tenant/ConsultationDetailPage";
import { ConsultationsPage } from "@/feature-module/tenant/ConsultationsPage";
import { PrescriptionsPage } from "@/feature-module/tenant/PrescriptionsPage";
import { NursingCarePage } from "@/feature-module/tenant/NursingCarePage";
import { HospitalizationPage } from "@/feature-module/tenant/HospitalizationPage";
import { PatientConsumablesPage } from "@/feature-module/tenant/PatientConsumablesPage";
import { LaboratoryPage } from "@/feature-module/tenant/LaboratoryPage";
import { LabResultsPage } from "@/feature-module/tenant/LabResultsPage";
import { ImagingPage } from "@/feature-module/tenant/ImagingPage";
import { ImagingReportsPage } from "@/feature-module/tenant/ImagingReportsPage";
import { AdminIntegrationsPage } from "@/feature-module/tenant/AdminIntegrationsPage";
import { AdminSettingsPage } from "@/feature-module/tenant/AdminSettingsPage";
import { AdminUsersPage } from "@/feature-module/tenant/AdminUsersPage";
import { HospitolSetupWizardPage } from "@/feature-module/tenant/setup/HospitolSetupWizardPage";
import { ComingSoonPage } from "@/feature-module/tenant/ComingSoonPage";
import { ModuleEmbedPage } from "@/feature-module/tenant/ModuleEmbedPage";
import { RhDepartmentsPage } from "@/feature-module/tenant/RhDepartmentsPage";
import { RhEmployeesPage } from "@/feature-module/tenant/RhEmployeesPage";
import { RhSchedulesPage } from "@/feature-module/tenant/RhSchedulesPage";
import {
  SupplyConsumablesArticlesPage,
  SupplyMedicinesArticlesPage,
} from "@/feature-module/tenant/SupplyArticlesPage";
import {
  SupplyConsumablesReceiptsPage,
  SupplyMedicinesReceiptsPage,
} from "@/feature-module/tenant/SupplyReceiptsPage";
import {
  SupplyConsumablesReceiptsHistoryPage,
  SupplyMedicinesReceiptsHistoryPage,
} from "@/feature-module/tenant/SupplyReceiptsHistoryPage";
import {
  SupplyConsumablesLevelsPage,
  SupplyMedicinesLevelsPage,
} from "@/feature-module/tenant/SupplyStockLevelsPage";
import { MODULE_TARGET_PATHS } from "@/core/module-paths";
import { HOSPITOL_UI_PERMISSIONS } from "@/core/permissions";
import { ProtectedRoute } from "./ProtectedRoute";
import { routes } from "./all_routes";
import { AuthLogoutSyncPage } from "@itaas/core/auth/AuthLogoutSyncPage";
import { DemoCaptureOverlay } from "@/demo-capture";

function soon(title: string, description: string, moduleLabel: string, sprint?: string) {
  return (
    <ComingSoonPage
      title={title}
      description={description}
      moduleLabel={moduleLabel}
      sprint={sprint}
    />
  );
}

export function AppRouter() {
  return (
    <>
    <Routes>
      <Route path={routes.login} element={<LoginPage />} />
      <Route path={routes.authHandoff} element={<AuthHandoffPage />} />
      <Route path={routes.authLogoutSync} element={<AuthLogoutSyncPage />} />
      <Route
        element={
          <ProtectedRoute>
            <TenantLayout />
          </ProtectedRoute>
        }
      >
        <Route path={routes.tenantDashboard} element={<PharmacyDashboardPage />} />
        <Route
          path={routes.dashboardFinance}
          element={
            <ProtectedRoute
              permissions={[
                ...HOSPITOL_UI_PERMISSIONS.dashboard,
                ...HOSPITOL_UI_PERMISSIONS.billing,
              ]}
            >
              <FinancialDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.dashboardOperations}
          element={
            <ProtectedRoute
              permissions={[
                ...HOSPITOL_UI_PERMISSIONS.dashboard,
                ...HOSPITOL_UI_PERMISSIONS.admissions,
              ]}
            >
              <OperationalDashboardPage />
            </ProtectedRoute>
          }
        />

        {/* —— Accueil & flux —— */}
        <Route
          path={routes.admissions}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.admissions]}>
              <AdmissionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.emergency}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.admissions]}>
              <EmergencyPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.ambulanceTransports}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.admissions]}>
              <AmbulanceTransportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.hospitalServicesHub}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.dashboard]}>
              <HospitalServicesHubPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.directionHub}
          element={
            <ProtectedRoute
              permissions={[
                ...HOSPITOL_UI_PERMISSIONS.dashboard,
                ...HOSPITOL_UI_PERMISSIONS.admin,
              ]}
            >
              <DirectionHubPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.appointments}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.admissions]}>
              <AppointmentsPage />
            </ProtectedRoute>
          }
        />

        {/* —— Patients —— */}
        <Route
          path={routes.patients}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.patients]}>
              <PatientsListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tenant/hospitol/patients/:patientId"
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.patients]}>
              <PatientDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.patientRecords}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.patients]}>
              <PatientRecordsPage />
            </ProtectedRoute>
          }
        />

        {/* —— Module Pharmacie (actif) —— */}
        <Route
          path={routes.pharmacyInvoice}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.pharmacy]}>
              <PharmacyInvoicePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.pharmacyOrders}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.pharmacy]}>
              <PharmacyOrdersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tenant/hospitol/pharmacy/orders/:prescriptionId"
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.pharmacy]}>
              <PharmacyOrderDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.pharmacyInvoiceHistory}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.pharmacy]}>
              <PharmacyInvoiceHistoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.pharmacyStockArticles}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.stock]}>
              <PharmacyStockArticlesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.pharmacyStockScan}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.pharmacy]}>
              <PharmacyStockScanPage />
            </ProtectedRoute>
          }
        />

        {/* —— Clinique —— */}
        <Route
          path={routes.consultations}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.clinical]}>
              <ConsultationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tenant/hospitol/clinical/consultations/:consultationId"
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.clinical]}>
              <ConsultationDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.prescriptions}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.clinical]}>
              <PrescriptionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.surgery}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.clinical]}>
              <SurgeryPage />
            </ProtectedRoute>
          }
        />

        {/* —— Soins infirmiers & hospitalisation —— */}
        <Route
          path={routes.nursingCare}
          element={
            <ProtectedRoute
              permissions={[
                ...HOSPITOL_UI_PERMISSIONS.nursing,
                ...HOSPITOL_UI_PERMISSIONS.nursingVitals,
              ]}
            >
              <NursingCarePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.hospitalization}
          element={
            <ProtectedRoute
              permissions={[
                ...HOSPITOL_UI_PERMISSIONS.hospitalization,
                ...HOSPITOL_UI_PERMISSIONS.bedsAssign,
              ]}
            >
              <HospitalizationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.patientConsumables}
          element={
            <ProtectedRoute
              permissions={[
                ...HOSPITOL_UI_PERMISSIONS.hospitalization,
                ...HOSPITOL_UI_PERMISSIONS.nursing,
              ]}
            >
              <PatientConsumablesPage />
            </ProtectedRoute>
          }
        />

        {/* —— Laboratoire & imagerie —— */}
        <Route
          path={routes.laboratory}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.laboratory]}>
              <LaboratoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.labResults}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.laboratory]}>
              <LabResultsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.imaging}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.imaging]}>
              <ImagingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.imagingReports}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.imaging]}>
              <ImagingReportsPage />
            </ProtectedRoute>
          }
        />

        {/* —— RH (composition native + console admin) —— */}
        <Route
          path={routes.rhEmployees}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.rh]}>
              <RhEmployeesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.rhDepartments}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.rh]}>
              <RhDepartmentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.rhSchedules}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.rh]}>
              <RhSchedulesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.rhAdminConsole}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.rh]}>
              <ModuleEmbedPage
                moduleId="rh"
                targetPath={MODULE_TARGET_PATHS.rh.employees}
                title="Console RH"
                compact
              />
            </ProtectedRoute>
          }
        />

        {/* —— Approvisionnement (composition native + console ISC) —— */}
        <Route
          path={routes.supplyMedicinesArticles}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.stock]}>
              <SupplyMedicinesArticlesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.supplyMedicinesLevels}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.stock]}>
              <SupplyMedicinesLevelsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.supplyMedicinesReceipts}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.stock]}>
              <SupplyMedicinesReceiptsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.supplyMedicinesReceiptsHistory}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.stock]}>
              <SupplyMedicinesReceiptsHistoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.supplyConsumablesArticles}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.stock]}>
              <SupplyConsumablesArticlesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.supplyConsumablesLevels}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.stock]}>
              <SupplyConsumablesLevelsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.supplyConsumablesReceipts}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.stock]}>
              <SupplyConsumablesReceiptsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.supplyConsumablesReceiptsHistory}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.stock]}>
              <SupplyConsumablesReceiptsHistoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.supplyAdminConsole}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.stock]}>
              <ModuleEmbedPage
                moduleId="inventory"
                targetPath={MODULE_TARGET_PATHS.inventory.articles}
                title="Console ISC"
                compact
              />
            </ProtectedRoute>
          }
        />

        {/* —— Assurance médicale —— */}
        <Route
          path={routes.insuranceHome}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.insurance]}>
              <InsuranceHomePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.insuranceInsurers}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.insurance]}>
              <InsuranceInsurersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tenant/hospitol/insurance/insurers/:insurerId"
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.insurance]}>
              <InsuranceInsurerDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.insuranceCases}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.insurance]}>
              <InsuranceCasesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tenant/hospitol/insurance/cases/:caseId"
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.insurance]}>
              <InsuranceCaseDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.insurancePartnerships}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.insuranceManage]}>
              <InsurancePartnershipsPage />
            </ProtectedRoute>
          }
        />

        {/* —— Facturation —— */}
        <Route
          path={routes.billingAccounting}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.billing]}>
              <BillingAccountingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.billingCashier}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.billing]}>
              <CashierPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.billingInvoices}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.billing]}>
              <PharmacyBillingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.billingAdminConsole}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.billing]}>
              <ModuleEmbedPage
                moduleId="billing"
                targetPath={MODULE_TARGET_PATHS.billing.invoices}
                title="Console Kanakku"
                compact
              />
            </ProtectedRoute>
          }
        />

        {/* —— Administration —— */}
        <Route path={routes.adminMenuDemo} element={<AdminMenuVisibilityPage />} />
        <Route
          path={routes.hospitolSetup}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.admin]}>
              <HospitolSetupWizardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.adminUsers}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.admin]}>
              <AdminUsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.adminSettings}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.admin]}>
              <AdminSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.adminIntegrations}
          element={
            <ProtectedRoute permissions={[...HOSPITOL_UI_PERMISSIONS.admin]}>
              <AdminIntegrationsPage />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to={routes.tenantDashboard} replace />} />
    </Routes>
    <DemoCaptureOverlay />
    </>
  );
}
